import React from 'react';
import { connect } from 'react-redux';
import Box from '@bit/knotchinc.web-react-components.box';
import SlideToggleWithIcon from '@bit/knotchinc.web-react-components.slide-toggle-with-icon';
import StoreSync from '@bit/knotchinc.web-react-components.store-sync';
import KeywordSearchLink from 'components/Global/Tag/KeywordSearchLink.jsx';
import { formatTime, numberWithCommas } from 'helpers/Utils.js';
import { Page } from '@bit/knotchinc.web-react-components.page';
import Background from '@bit/knotchinc.web-react-components.background';
import EntityItem from 'components/Global/EntityItem/EntityItem.jsx';
import { createBookmark, removeBookmark } from 'components/Pages/Settings/actions.js';
import Mixpanel from 'helpers/Mixpanel.js';
import {
  StyledPageTitle,
  StyledInfoColumn,
  StyledTopSection,
  StyledWidthContainer,
  ContentScreenshot,
  ArticleContent
} from '@bit/knotchinc.web-react-components.content-view';
import { fetchContentMetadata } from './actions';
import { ContentActionContainer, ContentActionButton, ContentType, PageLink, HighlightKeywordsContainer, HighlightToggleContainer } from './styled.js';

const ContentPage = ({ contentMetadata, openScreenshot, toggleBookmark, localBookmarked, closePopup, toggleHiglightedKeywords, showHighlightedKeywords }) => {
  const {
    title,
    public_id,
    brand,
    content_type,
    publisher,
    page_url,
    keyword_tags,
    num_words,
    category = {},
    hero_image_url,
    page_screenshot_url: url,
    page_screenshot_thumbnail_url: thumbnail,
  } = contentMetadata;

  const counts = [
    {
      text: 'Content Type',
      value: (
        <ContentType type={content_type}>
          {content_type}
        </ContentType>
      ),
    },
    { text: 'Word Count', value: numberWithCommas(num_words) || 'N/A' },
    {
      text: 'Reading time',
      value: num_words ? formatTime(num_words / 3.33) : 'N/A',
    },
    // { text: 'Article Length', value: article_length || 'N/A' },
    { text: 'Category', value: category.value || 'N/A' },
  ];

  const keywordsList = keyword_tags && keyword_tags.map(keyword =>
    keyword && <KeywordSearchLink key={keyword.value} data={keyword} onClick={() => closePopup()} />);

  return (
    <React.Fragment>
      <Page>
        <StyledTopSection>
          <Background url={hero_image_url} showGradient opacity={0.9} />
          <StyledWidthContainer>
            <StyledInfoColumn>
              <StyledPageTitle>{title}</StyledPageTitle>
              {page_url ? (
                <Box mb="32px">
                  <PageLink href={page_url} target="_blank">
                    {page_url}
                  </PageLink>
                </Box>
              ) : null}
              <Box mb="32px">
                {brand && (
                  <Box display="inline-block">
                    <EntityItem data={brand} />
                  </Box>
                )}
                {publisher.public_id && (
                  <Box display="inline-block">
                    <EntityItem data={publisher} />
                  </Box>
                )}
              </Box>
              {keywordsList && <Box mb="32px">{keywordsList}</Box>}
              {keywordsList && (
                <Box mb="32px">
                  <HighlightKeywordsContainer>
                    HIGHLIGHT KEYWORDS IN CONTENT BELOW
                  </HighlightKeywordsContainer>
                  <HighlightToggleContainer>
                    <SlideToggleWithIcon
                      backgroundColorForUntoggledState="#CBCED6"
                      toggled={showHighlightedKeywords}
                      toggleHandler={() => toggleHiglightedKeywords()}
                    />
                  </HighlightToggleContainer>
                </Box>
              )}
            </StyledInfoColumn>
            <ContentScreenshot
              {...{ url, thumbnail }}
              fetched
              onClick={() => {
                if (thumbnail) {
                  Mixpanel.track("expanded page screenshot");
                  openScreenshot(url);
                }
              }}
            />
          </StyledWidthContainer>
        </StyledTopSection>
      </Page>
      <ArticleContent
        showHighlightedKeywords={showHighlightedKeywords}
        data={contentMetadata}
        metrics={counts}
        headerComponent={
          <Box pr="36px" width="40%" display="inline-block" verticalAlign="top">
            <ContentActionContainer>
              {/* <ContentActionButton type="send">Send</ContentActionButton> */}
              {page_url && (
                <ContentActionButton
                  type="view"
                  as="a"
                  target="_blank"
                  href={page_url}
                >
                  View
                </ContentActionButton>
              )}
              <ContentActionButton
                type="save"
                active={localBookmarked}
                onClick={() => toggleBookmark(public_id)}
              >
                {localBookmarked ? `Saved` : `Save`}
              </ContentActionButton>
            </ContentActionContainer>
          </Box>
        }
      />
    </React.Fragment>
  );
};

// eslint-disable react/no-danger
class ContentView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      screenshotOpen: false,
      offset: 0,
      hideBottomBar: false,
      showHighlightedKeywords: false
    };
  }

  componentDidMount() {
    const { id, getContentData } = this.props;
    getContentData(id);
    Mixpanel.track(`view Content Popup`);
  }

  toggleBookmark(id) {
    Mixpanel.track('clicked Bookmark');
    const { bookmark, rmvBookmark, setLocalBookmark, localBookmarked } = this.props;

    if (localBookmarked) {
      rmvBookmark(id);
      setLocalBookmark(false);
    } else {
      bookmark(id);
      setLocalBookmark(true);
    }
  }

  toggleHiglightedKeywords(){
    this.setState(prevState => ({
      showHighlightedKeywords: !prevState.showHighlightedKeywords
    }));
  }

  render() {
    const { contentMetadata, openScreenshot, localBookmarked, closePopup } = this.props;

    return (
      <StoreSync stores={{ contentMetadata }}>
        <ContentPage
          openScreenshot={openScreenshot}
          toggleBookmark={id => this.toggleBookmark(id)}
          localBookmarked={localBookmarked}
          closePopup={closePopup}
          toggleHiglightedKeywords={() => this.toggleHiglightedKeywords()}
          showHighlightedKeywords={this.state.showHighlightedKeywords}
        />
      </StoreSync>
    );
  }
}

const mapStateToProps = ({ Content: { contentMetadata } }) => ({
  contentMetadata,
});

const mapDispatchToProps = dispatch => ({
  getContentData: contentId => dispatch(fetchContentMetadata(contentId)),
  bookmark: (id) => dispatch(createBookmark(id)),
  rmvBookmark: (id) => dispatch(removeBookmark(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentView);
